import React from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../../../i18n';
import MarketingHeader from '../common/MarketingHeader';
import MarketingFooter from '../common/MarketingFooter';
import GuideCta from '../common/GuideCta';
import CtaBand from '../Home/CtaBand';
import { BUDGET_SIGNUP_URL } from '../signup/startIntent';
import DrinksCalculator from './DrinksCalculator';
import { bottlesFor, computeDrinks, defaultSettings, DEFAULT_MARGIN, DRINKS } from './drinksModel';
import { FAQ_STATIC, PAIRINGS, REFERENCE_GUESTS, RULES, SAVINGS } from './content';
import '../../../styles/marketing.css';
import '../../../styles/marketing-guide.css';

// Tableau de référence : quantités proposées par l'outil (vin d'honneur, dîner, brunch)
const REFERENCE = REFERENCE_GUESTS.map((n) => ({ guests: n, ...computeDrinks(defaultSettings(n)) }));

// FAQ chiffrée depuis le modèle de l'outil (100 invités, quantités proposées)
const CHAMPAGNE_100 = bottlesFor(100, 3, DEFAULT_MARGIN); // 2 flûtes au cocktail + 1 au dessert
const WINE_DINNER_100 = bottlesFor(100, 2, DEFAULT_MARGIN) + bottlesFor(100, 1, DEFAULT_MARGIN);
const FAQ = [
  {
    q: 'Combien de bouteilles de champagne pour 100 personnes ?',
    a: `Environ ${CHAMPAGNE_100} bouteilles pour 2 flûtes au vin d’honneur et 1 au dessert, marge de 10 % comprise. Sans champagne au dessert, comptez environ ${bottlesFor(100, 2, DEFAULT_MARGIN)} bouteilles.`,
  },
  {
    q: 'Combien de bouteilles de vin pour 100 personnes au dîner ?',
    a: `Environ ${WINE_DINNER_100} bouteilles pour 3 verres par personne (2 de rouge, 1 de blanc), marge comprise. Ajustez avec le calculateur selon votre menu.`,
  },
  ...FAQ_STATIC,
];

const LD = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
});

// Page « Calculateur de boissons mariage » : l'outil en haut, puis repères,
// accords, astuces et FAQ. Pré-rendue au build (réglages par défaut).
const DrinksToolPage: React.FC = () => {
  const t = useT();
  return (
    <div className="mk-page">
      <MarketingHeader>
        <Link to="/login" className="mkt-btn ghost">
          {t('Se connecter')}
        </Link>
        <Link to="/signup" className="mkt-btn primary mk-header-signup">
          {t('Créer mon espace')}
        </Link>
      </MarketingHeader>

      <main className="mk-main" lang="fr">
        <section className="mk-hero mk-container mg-hero-compact">
          <div className="mk-hero-text">
            <span className="mk-eyebrow">Calculateur de boissons</span>
            <h1 className="mk-hero-title">
              Combien de bouteilles pour votre mariage ? <em>Le calcul, moment par moment.</em>
            </h1>
            <p className="mk-lead">
              Vin d’honneur, dîner, brunch : choisissez ce que vous servez à chaque moment, le nombre de verres par
              personne, et obtenez le nombre de bouteilles de rouge, de blanc, de rosé et de champagne.
            </p>
          </div>
        </section>

        <section className="mk-container mg-tool-section" aria-label="Calculateur">
          <DrinksCalculator />
        </section>

        <article className="mg-article mk-container">
          <section id="reference">
            <h2>Combien de bouteilles pour 50, 80, 100 ou 150 invités ?</h2>
            <p>
              Avec les quantités proposées par le calculateur, pour un vin d’honneur, un dîner et un brunch avec le même
              nombre d’invités adultes, marge de 10 % comprise :
            </p>
            <div className="mg-table-scroll">
              <table className="mg-table mg-table-split mg-table-compact">
                <thead>
                  <tr>
                    <th scope="col">Invités</th>
                    {DRINKS.map((d) => (
                      <th key={d.key} scope="col">
                        {d.label}
                      </th>
                    ))}
                    <th scope="col">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {REFERENCE.map((r) => (
                    <tr key={r.guests}>
                      <td>{r.guests} invités</td>
                      {DRINKS.map((d) => (
                        <td key={d.key}>{r.totals[d.key]}</td>
                      ))}
                      <td>
                        <b>{r.total}</b>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p>Pas de brunch, ou pas de rosé ? Décochez-le dans le calculateur : le total se met à jour.</p>
          </section>

          <section id="reperes">
            <h2>Les repères à retenir</h2>
            {RULES.map((rule) => (
              <div key={rule.title} className="mg-admin">
                <h3>{rule.title}</h3>
                <p>{rule.text}</p>
              </div>
            ))}
          </section>

          <GuideCta
            to={BUDGET_SIGNUP_URL}
            title="Les boissons, dans votre budget"
            text="Ajoutez vos achats de vin et de champagne au budget, et ajustez les quantités avec le nombre d’invités qui ont répondu « présent »."
            label="Créer mon espace gratuit"
          />

          <section id="accords">
            <h2>Quel vin servir à chaque moment ?</h2>
            {PAIRINGS.map((p) => (
              <div key={p.title} className="mg-admin">
                <h3>{p.title}</h3>
                <p>{p.text}</p>
              </div>
            ))}
          </section>

          <section id="economies">
            <h2>Payer moins cher ses boissons de mariage</h2>
            <ol className="mg-steps">
              {SAVINGS.map((s) => (
                <li key={s.title}>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </li>
              ))}
            </ol>
            <p>
              Et pour l’ensemble des dépenses, voir notre guide du{' '}
              <Link to="/budget-mariage">budget de mariage, poste par poste</Link>.
            </p>
          </section>

          <section id="faq">
            <h2>Questions fréquentes</h2>
            <div className="mg-faq">
              {FAQ.map(({ q, a }) => (
                <details key={q}>
                  <summary>{q}</summary>
                  <p>{a}</p>
                </details>
              ))}
            </div>
          </section>

          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: LD }} />
        </article>

        <CtaBand />
      </main>

      <MarketingFooter />
    </div>
  );
};

export default DrinksToolPage;
